import React, { useEffect, useRef, useState } from "react";
import { Technic, Workshop } from "../types";
import { technicService } from "../services/dataService";
import { poErr } from "../utils/purchasing";
import { PI, useLayerEsc, useRestoreFocus } from "./PickerModal";

interface CreateTechnicModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTechnicCreated: (technic: Technic) => void;
  workshops: Workshop[];
}

const emptyTechnic = { name: "", description: "", category: "", workshopId: "" };

const CreateTechnicForm: React.FC<Omit<CreateTechnicModalProps, "isOpen">> = ({ onClose, onTechnicCreated, workshops }) => {
  const [newTechnic, setNewTechnic] = useState(emptyTechnic);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const firstRef = useRef<HTMLInputElement>(null);
  useRestoreFocus();
  useEffect(() => { firstRef.current?.focus(); }, []);

  const handleClose = () => { if (!isSubmitting) onClose(); };
  useLayerEsc(handleClose);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || !newTechnic.name.trim()) return;
    setIsSubmitting(true);
    setError("");
    try {
      const createdTechnic = await technicService.create(newTechnic);
      onTechnicCreated(createdTechnic);
      onClose();
    } catch (err) {
      console.error("Teknik oluşturulurken hata:", err);
      setError(poErr(err, "Teknik oluşturulurken bir hata oluştu."));
      setIsSubmitting(false);
    }
  };

  const set = (k: keyof typeof emptyTechnic, v: string) => { setNewTechnic((p) => ({ ...p, [k]: v })); setError(""); };

  return (
    <div className="po-overlay center top pk-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <form className="po-modal pk-modal fit" role="dialog" aria-modal="true" aria-labelledby="pk-ct-title" onSubmit={handleSubmit}>
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">Teknik</span>
            <h3 id="pk-ct-title">Yeni teknik</h3>
          </div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={handleClose} disabled={isSubmitting}>{PI.close}</button>
        </div>

        <div className="po-modal-body">
          <p className="po-form-hint">Oluşturulan teknik siparişin seçili tekniklerine eklenir.</p>
          {error && <div className="po-note err" role="alert">{error}</div>}
          <div className="po-grid2">
            <label className="po-field">
              <span>Teknik adı<em>zorunlu</em></span>
              <input ref={firstRef} value={newTechnic.name} required disabled={isSubmitting} placeholder="Örn. Dijital baskı" onChange={(e) => set("name", e.target.value)} />
            </label>
            <label className="po-field">
              <span>Kategori</span>
              <input value={newTechnic.category} disabled={isSubmitting} placeholder="Örn. Baskı teknikleri" onChange={(e) => set("category", e.target.value)} />
            </label>
            <label className="po-field span2">
              <span>Atölye</span>
              <select value={newTechnic.workshopId} disabled={isSubmitting} onChange={(e) => set("workshopId", e.target.value)}>
                <option value="">Seçilmedi</option>
                {workshops.map((ws) => <option key={ws.workshopId} value={ws.workshopId}>{ws.name}</option>)}
              </select>
            </label>
            <label className="po-field span2">
              <span>Açıklama</span>
              <textarea rows={3} value={newTechnic.description} disabled={isSubmitting} placeholder="Teknik hakkında kısa bir açıklama" onChange={(e) => set("description", e.target.value)} />
            </label>
          </div>
        </div>

        <div className="po-modal-foot">
          <button type="button" className="po-btn" onClick={handleClose} disabled={isSubmitting}>Vazgeç</button>
          <button type="submit" className="po-btn primary" disabled={isSubmitting || !newTechnic.name.trim()}>{isSubmitting ? "Oluşturuluyor…" : "Oluştur"}</button>
        </div>
      </form>
    </div>
  );
};

/** Yeni teknik formu (Sipariş formundaki teknik seçiciden açılır). Her açılışta boş başlar. */
const CreateTechnicModal: React.FC<CreateTechnicModalProps> = ({ isOpen, ...rest }) => (isOpen ? <CreateTechnicForm {...rest} /> : null);

export default CreateTechnicModal;
